<script setup>
import { usePage } from '@inertiajs/vue3'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

// App-download banner (SUB-MVP-66). Store URLs come from the server (config ←
// env), so they are set once the app is published. A store button only renders
// when its URL is present, and the whole banner hides if neither is set — so it
// is safe even if the flag is switched on before the links exist.
const page = usePage()
const appStoreUrl = computed(() => page.props.appDownload?.appStore || '')
const playStoreUrl = computed(() => page.props.appDownload?.playStore || '')
const hasAnyStore = computed(() => !!appStoreUrl.value || !!playStoreUrl.value)

// Auto-advancing carousel of real app screens. The interval lives in a ref so
// teardown stays const-only; it only runs client-side (onMounted) and sits out
// when the visitor prefers reduced motion.
const SCREEN_COUNT = 5
const current = ref(0)
const timer = ref(null)
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

const stop = () => {
  if (timer.value) {
    clearInterval(timer.value)
    timer.value = null
  }
}
const start = () => {
  if (prefersReducedMotion()) return
  stop()
  timer.value = window.setInterval(() => {
    current.value = (current.value + 1) % SCREEN_COUNT
  }, 3400)
}
const goTo = (index) => {
  current.value = index
  start()
}

onMounted(start)
onBeforeUnmount(stop)
</script>

<template>
  <section
    v-if="hasAnyStore"
    dusk="app-download-banner"
    class="mx-auto grid max-w-5xl overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-stone-200 lg:grid-cols-[1.15fr_1fr]"
  >
    <!-- Pitch -->
    <div class="flex flex-col justify-center gap-6 p-8 sm:p-10">
      <div class="flex items-center gap-4">
        <div class="flex size-14 flex-none items-center justify-center rounded-2xl bg-indigo-600 shadow-sm">
          <svg class="size-7 text-white" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="6" y="2" width="12" height="20" rx="3" stroke="currentColor" stroke-width="1.7" />
            <path d="M10.5 18.5h3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
          </svg>
        </div>
        <div>
          <h2 class="text-2xl font-bold tracking-tight text-gray-900">Take ABN Register with you</h2>
          <p class="mt-1 text-sm text-stone-500">Free on the App Store and Google Play</p>
        </div>
      </div>

      <p class="max-w-md text-base leading-7 text-stone-600">
        Register, track and manage your ABN, GST and ABN Books from your phone, and get a
        heads-up the moment your tax agent needs something.
      </p>

      <div class="flex flex-col gap-3 sm:flex-row">
        <a
          v-if="appStoreUrl"
          :href="appStoreUrl"
          target="_blank"
          rel="noopener"
          dusk="app-store-link"
          class="flex items-center gap-3 rounded-xl bg-gray-900 px-5 py-2.5 text-white transition hover:bg-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
        >
          <svg class="size-7" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.5 43-17.8 75.8-17.8 31.8 0 48.3 17.8 76.4 17.8 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
          </svg>
          <span>
            <span class="block text-[11px] leading-none text-stone-300">Download on the</span>
            <span class="block text-lg font-semibold leading-tight">App Store</span>
          </span>
        </a>

        <a
          v-if="playStoreUrl"
          :href="playStoreUrl"
          target="_blank"
          rel="noopener"
          dusk="play-store-link"
          class="flex items-center gap-3 rounded-xl bg-gray-900 px-5 py-2.5 text-white transition hover:bg-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
        >
          <svg class="size-6" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#00D2FF" d="M3.6 2.2 13.3 12 3.6 21.8a1.7 1.7 0 0 1-.6-1.3V3.5c0-.5.2-1 .6-1.3Z" />
            <path fill="#00E676" d="M3.6 2.2c.4-.2.9-.2 1.4.1l11.7 6.6-2.9 2.9L3.6 2.2Z" />
            <path fill="#FFCE00" d="M16.7 8.9 20 10.8c.9.5.9 1.9 0 2.4l-3.3 1.9-3-3 3-3.2Z" />
            <path fill="#FF3D47" d="m3.6 21.8 10.2-9.9 2.9 2.9L5 21.7c-.5.3-1 .3-1.4.1Z" />
          </svg>
          <span>
            <span class="block text-[11px] leading-none text-stone-300">Get it on</span>
            <span class="block text-lg font-semibold leading-tight">Google Play</span>
          </span>
        </a>
      </div>
    </div>

    <!-- Product peek: a phone cycling through real app screens -->
    <div
      class="relative flex flex-col items-center justify-center gap-6 overflow-hidden bg-stone-100 px-8 py-10"
      @mouseenter="stop"
      @mouseleave="start"
    >
      <div class="absolute -right-12 -top-12 size-56 rounded-full bg-indigo-200/60 blur-3xl" aria-hidden="true"></div>
      <div class="absolute -bottom-16 -left-10 size-48 rounded-full bg-indigo-100/50 blur-3xl" aria-hidden="true"></div>

      <div class="phone-float relative">
        <div class="absolute inset-x-6 -bottom-5 h-8 rounded-full bg-indigo-950/20 blur-xl" aria-hidden="true"></div>
        <div class="phone-frame relative w-[236px] rounded-[2.6rem] bg-gray-900 p-[9px] ring-1 ring-white/10">
          <div class="relative overflow-hidden rounded-[2.1rem] bg-white" style="aspect-ratio: 414 / 900">
            <div class="track flex h-full" :style="{ transform: `translateX(-${current * 100}%)` }">
              <img src="@img/app/account.png" alt="ABN Register app account screen" class="w-full flex-none select-none" draggable="false" />
              <img src="@img/app/home.png" alt="ABN Register app home screen" class="w-full flex-none select-none" draggable="false" />
              <img src="@img/app/composer.png" alt="ABN Register app new-message screen" class="w-full flex-none select-none" draggable="false" />
              <img src="@img/app/documents.png" alt="ABN Register app documents screen" class="w-full flex-none select-none" draggable="false" />
              <img src="@img/app/messages.png" alt="ABN Register app messages screen" class="w-full flex-none select-none" draggable="false" />
            </div>
          </div>
        </div>
      </div>

      <div class="flex gap-2">
        <button
          v-for="index in SCREEN_COUNT"
          :key="index"
          type="button"
          class="h-2 rounded-full transition-all duration-300"
          :class="current === index - 1 ? 'bg-indigo-600' : 'bg-stone-300'"
          :style="{ width: current === index - 1 ? '26px' : '8px' }"
          :aria-label="`Show app screen ${index}`"
          @click="goTo(index - 1)"
        ></button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.track {
  transition: transform 0.65s cubic-bezier(0.22, 1, 0.36, 1);
}
.phone-frame {
  box-shadow:
    0 28px 60px -15px rgba(30, 27, 75, 0.5),
    0 8px 20px -8px rgba(0, 0, 0, 0.3);
}
.phone-float {
  animation: float 5.5s ease-in-out infinite;
}
@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-9px);
  }
}
@media (prefers-reduced-motion: reduce) {
  .track {
    transition: none;
  }
  .phone-float {
    animation: none;
  }
}
</style>
